import { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Mail, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useShop } from "@/context/ShopContext";
import { Logo } from "./Logo";

const TikTok = (p) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" {...p}><path d="M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 1 1-2.59-2.59c.27 0 .53.04.77.12V9.77a5.7 5.7 0 1 0 4.91 5.63V9.01a7.3 7.3 0 0 0 4.27 1.37V7.3a4.28 4.28 0 0 1-3.21-1.48z" /></svg>
);
const Pinterest = (p) => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" {...p}><path d="M12 2a10 10 0 0 0-3.64 19.31c-.09-.79-.17-2 .03-2.86l1.2-5.08s-.3-.61-.3-1.5c0-1.41.82-2.46 1.83-2.46.87 0 1.29.65 1.29 1.43 0 .87-.55 2.17-.84 3.38-.24 1.01.51 1.84 1.51 1.84 1.81 0 3.2-1.91 3.2-4.66 0-2.44-1.75-4.14-4.25-4.14-2.9 0-4.6 2.17-4.6 4.42 0 .87.34 1.81.76 2.32a.3.3 0 0 1 .07.29l-.28 1.15c-.05.18-.15.22-.34.13-1.27-.59-2.06-2.44-2.06-3.93 0-3.2 2.33-6.14 6.71-6.14 3.52 0 6.26 2.51 6.26 5.86 0 3.5-2.2 6.31-5.27 6.31-1.03 0-1.99-.53-2.32-1.17l-.63 2.4c-.23.88-.85 1.98-1.26 2.66A10 10 0 1 0 12 2z" /></svg>
);

const LINKS = [
  { to: "/contact", label: "Contact", id: "contact" },
  { to: "/policies/shipping", label: "Shipping Information", id: "shipping" },
  { to: "/size-guide", label: "Size Guide", id: "size-guide" },
  { to: "/policies/returns", label: "Returns & Refunds", id: "returns" },
  { to: "/policies/privacy", label: "Privacy Policy", id: "privacy" },
  { to: "/policies/terms", label: "Terms & Conditions", id: "terms" },
];

export const Footer = () => {
  const { settings } = useShop();
  const [email, setEmail] = useState("");
  const s = settings || {};
  const socials = [
    { url: s.instagram_url, Icon: Instagram, id: "instagram", label: "Instagram" },
    { url: s.tiktok_url, Icon: TikTok, id: "tiktok", label: "TikTok" },
    { url: s.pinterest_url, Icon: Pinterest, id: "pinterest", label: "Pinterest" },
    { url: s.contact_email ? `mailto:${s.contact_email}` : "", Icon: Mail, id: "email", label: "Email" },
  ].filter((x) => x.url);

  return (
    <footer data-testid="site-footer" className="bg-[var(--mm-red)] text-[var(--mm-cream)] relative overflow-hidden grain">
      <div className="px-5 sm:px-10 lg:px-16 pt-20 pb-10 relative">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <p className="eyebrow opacity-80 mb-5">The letter</p>
            <h3 className="font-serif-mm text-4xl sm:text-5xl leading-none mb-8">First to know, <em>always.</em></h3>
            <form onSubmit={(e) => { e.preventDefault(); if (email) { toast.success("Thank you — you're on the list."); setEmail(""); } }} className="flex border-b border-[var(--mm-cream)]/50 max-w-md">
              <input data-testid="newsletter-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Your email address" className="flex-1 bg-transparent py-3 outline-none placeholder:text-[var(--mm-cream)]/60 text-sm" />
              <button data-testid="newsletter-submit-button" aria-label="Subscribe" className="px-2 hover:translate-x-1 transition-transform"><ArrowRight size={18} /></button>
            </form>
          </div>
          <div className="lg:col-span-3 lg:col-start-7">
            <p className="eyebrow opacity-80 mb-5">Client care</p>
            <ul className="space-y-3 text-sm">
              {LINKS.map((l) => (
                <li key={l.id}><Link to={l.to} data-testid={`footer-${l.id}-link`} className="hover:opacity-70 transition-opacity">{l.label}</Link></li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-3">
            <p className="eyebrow opacity-80 mb-5">Follow</p>
            <a data-testid="footer-social-handle" href={s.instagram_url || "#"} target="_blank" rel="noreferrer" className="font-serif-mm italic text-3xl hover:opacity-70 transition-opacity">{s.instagram_handle || "@maisonmadison.co"}</a>
            <div className="flex gap-3 mt-6">
              {socials.map(({ url, Icon, id, label }) => (
                <a key={id} href={url} target="_blank" rel="noreferrer" aria-label={label} data-testid={`footer-${id}-icon`} className="w-10 h-10 border border-[var(--mm-cream)]/50 rounded-full flex items-center justify-center hover:bg-[var(--mm-cream)] hover:text-[var(--mm-red)] transition-colors">
                  <Icon size={17} />
                </a>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-20 flex justify-center"><Logo variant="cream" className="w-full max-w-3xl h-auto" testId="footer-logo-img" /></div>
        <div className="mt-10 pt-6 border-t border-[var(--mm-cream)]/30 flex flex-col sm:flex-row justify-between gap-3 text-xs opacity-80">
          <span className="flex items-center gap-4">
            <Link to="/admin/login" data-testid="footer-admin-link" className="border border-[var(--mm-cream)]/40 px-2.5 py-1 text-[0.6rem] tracking-[0.2em] uppercase hover:bg-[var(--mm-cream)] hover:text-[var(--mm-red)] transition-colors">Admin</Link>
            © {new Date().getFullYear()} Maison Madison. All rights reserved.
          </span>
          <span data-testid="footer-handle-small">{s.instagram_handle || "@maisonmadison.co"}</span>
        </div>
      </div>
    </footer>
  );
};
